import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconChevronRight } from '@sorare/core/src/atoms/icons/system/iconChevronRight.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import type {
  MonetaryAmount,
  MonetaryAmountParams,
} from '@sorare/core/src/lib/monetaryAmount';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import type { StatsRarity } from '@sorare/core/src/lib/playerSearchView';
import { PriceDisplay } from '@sorare/core/src/lib/playerSearchView';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css } from '@sorare/css';

import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type {
  PriceCell_anyPlayer,
  PriceCell_commonPlayer,
} from './__generated__/index.graphql.ts';

export const priceCell = css`
  width: 100%;
  padding-inline: var(--market-value-padding);
  border-left: 1px solid var(--c-nd-150-opaque);
  @media ${laptopAndAbove} {
    position: sticky;
    background-color: var(--current-row-color);
    right: 0;
    z-index: 1;
  }
`;

/* Shared by the body cell and the header labels so both columns stay aligned. */
export const priceColumn = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;

export type MarketValues = Pick<
  PriceCell_commonPlayer,
  | 'limitedMarketValue'
  | 'rareMarketValue'
  | 'superRareMarketValue'
  | 'uniqueMarketValue'
>;

type FloorPriceCards = Pick<
  PriceCell_anyPlayer,
  | 'limitedFloorPriceCard'
  | 'rareFloorPriceCard'
  | 'superRareFloorPriceCard'
  | 'uniqueFloorPriceCard'
>;

type FloorPriceCard = NonNullable<FloorPriceCards['limitedFloorPriceCard']>;

const MARKET_VALUE_KEY_BY_RARITY = {
  [Rarity.limited]: 'limitedMarketValue',
  [Rarity.rare]: 'rareMarketValue',
  [Rarity.super_rare]: 'superRareMarketValue',
  [Rarity.unique]: 'uniqueMarketValue',
} as const satisfies Record<StatsRarity, keyof MarketValues>;

const FLOOR_PRICE_CARD_KEY_BY_RARITY = {
  [Rarity.limited]: 'limitedFloorPriceCard',
  [Rarity.rare]: 'rareFloorPriceCard',
  [Rarity.super_rare]: 'superRareFloorPriceCard',
  [Rarity.unique]: 'uniqueFloorPriceCard',
} as const satisfies Record<StatsRarity, keyof FloorPriceCards>;

const messages = defineMessages({
  viewListings: {
    defaultMessage: 'View listings',
  },
});

const ViewListingsPill = ({ onClick }: { onClick: () => void }) => (
  <Button
    size="small"
    color="transparent"
    onClick={onClick}
    endIcon={<FontAwesomeIcon icon={iconChevronRight} size="sm" />}
  >
    <FormattedMessage {...messages.viewListings} />
  </Button>
);

const PricePill = ({
  monetaryAmount,
  rarity,
  onClick,
}: {
  monetaryAmount: MonetaryAmountParams | MonetaryAmount;
  rarity: StatsRarity;
  onClick: () => void;
}) => {
  const { main } = useAmountWithConversion({ monetaryAmount });

  return (
    <Button
      size="small"
      color="transparent"
      onClick={onClick}
      startIcon={<ScarcityIcon rarity={rarity} />}
      endIcon={<FontAwesomeIcon icon={iconChevronRight} size="sm" />}
    >
      {main}
    </Button>
  );
};

const FloorPricePill = ({
  card,
  rarity,
  onClick,
}: {
  card: Nullable<FloorPriceCard>;
  rarity: StatsRarity;
  onClick: () => void;
}) => {
  const singleSale = useGetTokenSingleSaleDetails(card);
  const primaryBuy = useIsPrimaryBuyLive(card);
  const monetaryAmount = (primaryBuy || singleSale)?.price;

  if (!monetaryAmount) return <ViewListingsPill onClick={onClick} />;

  return (
    <PricePill
      monetaryAmount={monetaryAmount}
      rarity={rarity}
      onClick={onClick}
    />
  );
};

type Props = {
  marketValues?: MarketValues;
  floorPriceCards?: FloorPriceCards;
  rarities: StatsRarity[];
  priceDisplay: PriceDisplay;
  onListingsClick: (rarity: StatsRarity) => void;
};

export const PriceCell = ({
  marketValues,
  floorPriceCards,
  rarities,
  priceDisplay,
  onListingsClick,
}: Props) => (
  <Horizontal gap={0.5} className={priceCell}>
    {rarities.map(rarity => {
      const marketValue = marketValues?.[MARKET_VALUE_KEY_BY_RARITY[rarity]];
      const floorPriceCard =
        floorPriceCards?.[FLOOR_PRICE_CARD_KEY_BY_RARITY[rarity]];
      const openListings = () => onListingsClick(rarity);

      if (priceDisplay === PriceDisplay.FLOOR_PRICE) {
        return (
          <div key={rarity} className={priceColumn}>
            <FloorPricePill
              card={floorPriceCard}
              rarity={rarity}
              onClick={openListings}
            />
          </div>
        );
      }

      return (
        <div key={rarity} className={priceColumn}>
          {marketValue ? (
            <PricePill
              monetaryAmount={marketValue}
              rarity={rarity}
              onClick={openListings}
            />
          ) : (
            <ViewListingsPill onClick={openListings} />
          )}
        </div>
      );
    })}
  </Horizontal>
);

PriceCell.fragments = {
  commonPlayer: gql`
    fragment PriceCell_commonPlayer on CommonPlayer {
      id
      limitedMarketValue: marketValue(
        rarity: limited
        seasonEligibility: $seasonEligibility
      ) @include(if: $withLimitedMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      rareMarketValue: marketValue(
        rarity: rare
        seasonEligibility: $seasonEligibility
      ) @include(if: $withRareMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      superRareMarketValue: marketValue(
        rarity: super_rare
        seasonEligibility: $seasonEligibility
      ) @include(if: $withSuperRareMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
      uniqueMarketValue: marketValue(
        rarity: unique
        seasonEligibility: $seasonEligibility
      ) @include(if: $withUniqueMarketValue) {
        ...MonetaryAmountFragment_monetaryAmount
      }
    }
    ${monetaryAmountFragment}
  ` as TypedDocumentNode<PriceCell_commonPlayer>,
  anyPlayer: gql`
    fragment PriceCell_anyPlayer on AnyPlayerInterface {
      slug
      limitedFloorPriceCard: lowestPriceAnyCard(
        rarity: limited
        inSeason: $inSeason
      ) @include(if: $withLimitedFloorPrice) {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
      rareFloorPriceCard: lowestPriceAnyCard(rarity: rare, inSeason: $inSeason)
        @include(if: $withRareFloorPrice) {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
      superRareFloorPriceCard: lowestPriceAnyCard(
        rarity: super_rare
        inSeason: $inSeason
      ) @include(if: $withSuperRareFloorPrice) {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
      uniqueFloorPriceCard: lowestPriceAnyCard(
        rarity: unique
        inSeason: $inSeason
      ) @include(if: $withUniqueFloorPrice) {
        slug
        ...useIsPrimaryBuyLive_anyCard
        ...useGetTokenSingleSaleDetails_anyCard
      }
    }
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
  ` as TypedDocumentNode<PriceCell_anyPlayer>,
};
